// BlogPopup.js
//
// PURPOSE:
// Displays blog post content in a modal popup with formatted HTML.
// Renders blog title, subtitle, and rich text content.
//
// SECURITY:
// - Uses DOMPurify to sanitize all HTML content before rendering
// - Prevents XSS (Cross-Site Scripting) attacks via malicious blog content
// - Strips dangerous tags like <script>, <iframe>, <object>
// - Removes event handlers like onclick, onerror, onload
// - Validates allowed HTML tags to safe formatting elements only
//
// ATTACK VECTOR PREVENTED:
// Without sanitization, a malicious admin could inject:
//   <script>document.cookie</script>
//   <img src=x onerror="alert('XSS')">
// This would execute JavaScript in visitors' browsers, allowing:
// - Session hijacking
// - Cookie theft
// - Phishing attacks
// - Malware distribution
//
// SANITIZATION STRATEGY:
// 1. Allow only safe formatting tags (p, h1-h6, strong, em, etc.)
// 2. Allow only safe attributes (href, src, alt, class, etc.)
// 3. Block all event handlers (onclick, onerror, etc.)
// 4. Remove script, iframe, object, embed, form elements
// 5. Sanitize title and subtitle as plain text (no HTML allowed)
//
// DEPENDENCIES:
// - DOMPurify: Industry-standard HTML sanitization library
// - Installation: npm install dompurify @types/dompurify
//
// USAGE:
// <BlogPopup blog={{ title, subtitle, content }} />

import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import './blogPopup.css';

const sanitizedHtmlConfig = {
    ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'pre', 'code',
        'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'span'
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    SAFE_FOR_TEMPLATES: true
};

function BlogPopup({ blog }) {
    const sanitizedContent = useMemo(
        () => DOMPurify.sanitize(blog?.content || '', sanitizedHtmlConfig),
        [blog?.content]
    );

    const sanitizedTitle = useMemo(
        () => DOMPurify.sanitize(blog?.title || '', { ALLOWED_TAGS: [], KEEP_CONTENT: true }),
        [blog?.title]
    );

    const sanitizedSubtitle = useMemo(
        () => DOMPurify.sanitize(blog?.subtitle || '', { ALLOWED_TAGS: [], KEEP_CONTENT: true }),
        [blog?.subtitle]
    );

    // Guard clause: return null if no blog data provided
    if (!blog) return null;

    return (
        <div className="blog-popup">
            <div className="blog-popup-header">
                {/* Render title and subtitle as plain text (no HTML) */}
                {/* Using curly braces ensures React escapes any remaining special characters */}
                <h1>{sanitizedTitle}</h1>
                <h2>{sanitizedSubtitle}</h2>
            </div>
            <div className="blog-popup-divider"></div>
            <div className="blog-popup-content">
                {/* 
                    SECURITY NOTE: Safe to use dangerouslySetInnerHTML here
                    Content has been sanitized by DOMPurify above
                    All malicious code has been removed
                    Only safe HTML formatting remains
                */}
                <div dangerouslySetInnerHTML={{ __html: sanitizedContent }}></div>
            </div>
        </div>
    );
}

export default BlogPopup;